import { useEffect, useRef, useState } from "react";

import type { Compare } from "~/tools/compare";
import { isStrictEqual } from "~/tools/is-strict-equal";
import { max } from "~/tools/max";
import { onTimeout } from "~/tools/on-timeout";

export interface UseDelayedOptions<T> {
	fallback?: T;
	/**
	 * @default isStrictEqual
	 */
	compare?: Compare<T>;
}

/**
 * A custom hook that returns a value after a specified delay.
 *
 * @template T - The type of the value.
 * @param {T} value - The value to be delayed.
 * @param {number} delay - The delay in milliseconds.
 * @param {UseDelayedOptions<T>} [options] - Optional configuration for the hook.
 * @param {T} [options.fallback=value] - The fallback value to use initially.
 * @param {(a: T, b: T) => boolean} [options.compare=isStrictEqual] - A function to compare the previous and current values.
 * @returns {T} - The delayed value.
 */
export function useDelayed<T>(
	value: T,
	delay: number,
	{ fallback = value, compare = isStrictEqual }: UseDelayedOptions<T> = {},
): T {
	const normalizedDelay = max(0, delay);
	const [delayedValue, setDelayedValue] = useState(fallback);

	const compareRef = useRef(compare);

	useEffect(() => {
		compareRef.current = compare;
	}, [compare]);

	useEffect(
		() =>
			onTimeout(() => {
				setDelayedValue((prev) => (compareRef.current(prev, value) ? prev : value));
			}, normalizedDelay),
		[value, normalizedDelay],
	);

	return delayedValue;
}
